import React, { FC, MouseEvent } from 'react';
import { accountRoles, subscriptionMakeBalancesLookup } from 'lib/constants';
import useTranslate from 'lib/languages/useTranslate';
import Button from 'components/shared/generic/button/presentational/Button';
import { useDispatch } from 'react-redux';
import { showModal } from 'actions/modal';
import {
    EXCESSIVE_FUNDS_MODAL,
    REGRADE_SUBSCRIPTION_MODAL,
} from 'constants/shared/modalTypes';
import { useRegradeSubscription } from 'lib/hooks';
import { useRestrictedAction } from '../../../../../hooks';
import { ReactComponent as LockIcon } from 'lib/_content/images/icons/lock.svg';
import { useGetAccountQuery } from 'lib/services';

interface Role {
    productClassCode: string;
    title: string;
    perks: { name: string, description: string }[];
}

interface SubscriptionCardProps {
    role: Role;
    productType: string;
    isCurrentSubscription: boolean;
}

const roleNames: Record<string, string> = {
    [accountRoles.Essential]: 'essential',
    [accountRoles.Premium]: 'premium',
    [accountRoles.PremiumPlus]: 'premiumPlus',
};

const SubscriptionCard: FC<SubscriptionCardProps> = ({
    role,
    productType,
    isCurrentSubscription,
}) => {
    const dispatch = useDispatch();
    const {
        isAccountRestricted,
        handleRestrictedAction,
    } = useRestrictedAction();
    const roleName = roleNames[role.productClassCode];

    const translate = useTranslate('pricePlan');
    const { data } = useGetAccountQuery();
    const availableBalance = data?.availableBalance ?? 0;
    const maxAmountAllowed = subscriptionMakeBalancesLookup[productType];

    const { isUpgrade, isDowngradeToEssential } = useRegradeSubscription(
        productType,
        role.productClassCode,
    );

    const handleOnClick = (e: MouseEvent<HTMLButtonElement>) => {
        handleRestrictedAction(() => {
            if (Number(availableBalance) > maxAmountAllowed) {
                showExcessiveFundsModal();
            } else {
                showConfirmModal();
            }
        });
    };
    const buttonText = translate(
        productType === role.productClassCode
            ? 'currentText'
            : isUpgrade
            ? 'upgrade'
            : 'downgrade',
    );

    return (
        <div className="subscription-item" key={role.productClassCode}>
            <h2 className="title">{role.title}</h2>
            <div className="benefits-list">
                {role.perks.map((benefit, i) => (
                    <span className="benefit" key={benefit.name + i}>
                        <p className="benefit-title">{benefit.name}</p>
                        <p className="benefit-text">{benefit.description}</p>
                    </span>
                ))}
            </div>

            {role.productClassCode !== accountRoles.Essential ||
            (role.productClassCode === accountRoles.Essential &&
                isCurrentSubscription) ? (
                <Button
                    disabled={
                        productType === role.productClassCode ||
                        isDowngradeToEssential
                    }
                    onClick={handleOnClick}
                    tracking
                    trackingLabel={`${roleName}_SUBSCRIPTION_CLICK`}
                    icon={
                        isAccountRestricted && (
                            <LockIcon
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    marginLeft: '8px',
                                }}
                            />
                        )
                    }
                >
                    {buttonText}
                </Button>
            ) : (
                <p className="downgrade-text">
                    {translate('customerServiceText')}
                </p>
            )}
        </div>
    );

    function showConfirmModal() {
        dispatch(
            showModal({
                type: REGRADE_SUBSCRIPTION_MODAL,
                props: {
                    currentRole: productType,
                    newRole: role.productClassCode,
                },
            }),
        );
    }

    function showExcessiveFundsModal() {
        dispatch(
            showModal({
                type: EXCESSIVE_FUNDS_MODAL,
            }),
        );
    }
};

export default SubscriptionCard;
